<template>
  <div class="ebm-requests">
    <ebm-transaction-stats-card
      :stats="statsData"
      :loading="statsLoading"
      :error="statsError"
      @refresh="refreshAllData"
      @showDateFilter="showDateFilter = true"
    />

    <DateFilter
      :isDark="isDarkMode"
      :isOpen="showDateFilter"
      :initialDateRange="dateRange"
      @close="closeDateFilter"
      @filter="handleDateRangeFilter"
    />

    <hs-card v-if="dateRange" class="ebm-requests__date-card" variant="soft">
      <div class="ebm-requests__date-copy">
        <hs-icon name="calendar" tone="primary" soft :size="17" />
        <div>
          <span>Filtered by date range</span>
          <strong>
            {{ formatDateRange(dateRange) }}
          </strong>
        </div>
      </div>
      <hs-button variant="secondary" icon="x-circle" @click="clearDateRange">
        Reset to today
      </hs-button>
    </hs-card>

    <ebm-transactions-table
      :transactions="transactionsData"
      :loading="transactionsLoading"
      :error="transactionsError"
      :pagination="pagination"
      @filter-change="handleFilterChange"
      @row-click="handleRowClick"
      @view-details="handleViewDetails"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    />

    <store-transaction-details-modal
      v-if="showDetailsModal"
      :transaction="selectedTransaction"
      :retrying="retryMutation.isPending.value"
      @close-modal="closeDetailsModal"
      @retry="handleRetryTransaction"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import DateFilter from "@/components/dashboard/DateFilter.vue";
import EbmTransactionStatsCard from "./EbmTransactionStatsCard.vue";
import EbmTransactionsTable from "../../modals/StoreTransactionsTable.vue";
import StoreTransactionDetailsModal from "@/components/modals/ebm/StoreTransactionDetailsModal.vue";
import { HsButton, HsCard, HsIcon } from "@/components/ui";
import {
  useStoreTransactions,
  useStoreTransactionStats,
  useRetryStoreTransaction,
  useRetryAllFailedStoreTransactions,
} from "@/composables/useTransactionQuery";

export default {
  name: "EbmRequests",
  components: {
    DateFilter,
    EbmTransactionStatsCard,
    EbmTransactionsTable,
    HsButton,
    HsCard,
    HsIcon,
    StoreTransactionDetailsModal,
  },
  props: {
    storeId: {
      type: [Number, String],
      required: true,
    },
  },
  setup(props) {
    // Reactive state
    const filters = ref({
      status: "",
      transaction_type: "",
      search: "",
    });
    const pagination = ref({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    });
    const showDetailsModal = ref(false);
    const selectedTransaction = ref(null);

    // Date range state - default to today's range
    const showDateFilter = ref(false);
    const today = new Date();
    const startDate = new Date(today);
    const endDate = new Date(today);

    // Set start time to 00:00:00
    startDate.setHours(0, 0, 0, 0);

    // Set end time to 23:59:59
    endDate.setHours(23, 59, 59, 999);

    const dateRange = ref({
      start: startDate,
      end: endDate,
    });

    // TanStack Query hooks
    const {
      data: transactionsData,
      isLoading: transactionsLoading,
      error: transactionsError,
      refetch: refetchTransactions,
    } = useStoreTransactions(
      computed(() => props.storeId),
      computed(() => {
        const params = {
          page: pagination.value.currentPage,
          per_page: pagination.value.itemsPerPage,
        };

        // Use magic_filter syntax for filtering
        if (filters.value.status) {
          params["status[eq]"] = filters.value.status;
        }
        if (filters.value.transaction_type) {
          params["transaction_type[eq]"] = filters.value.transaction_type;
        }
        let isExactSearch = false;

        if (filters.value.search) {
          const searchTerm = filters.value.search.trim();

          // Advanced parsing logic
          if (searchTerm) {
            // Check if it's a UUID-like pattern
            if (
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                searchTerm
              )
            ) {
              params["uuid[eq]"] = searchTerm;
              isExactSearch = true;
            }
            // Anything else uses json_like search
            else {
              params["transaction_metadata[json_like]"] = searchTerm;
            }
          }
        }

        // Add date range filters using magic-filter syntax (only if explicitly set and not doing exact search)
        if (dateRange.value && dateRange.value.start && !isExactSearch) {
          // Set time to 00:00:00 and convert to UTC
          const startDate = new Date(dateRange.value.start);
          startDate.setHours(0, 0, 0, 0);

          // Use the ISO string directly - it's already in UTC
          params["created_at[gte]"] = startDate.toISOString();
        }
        if (dateRange.value && dateRange.value.end && !isExactSearch) {
          // Set time to 23:59:59 and convert to UTC
          const endDate = new Date(dateRange.value.end);
          endDate.setHours(23, 59, 59, 999);

          // Use the ISO string directly - it's already in UTC
          params["created_at[lte]"] = endDate.toISOString();
        }

        return params;
      }),
      {}
    );

    const {
      data: statsData,
      isLoading: statsLoading,
      error: statsError,
      refetch: refetchStats,
    } = useStoreTransactionStats(
      computed(() => props.storeId),
      computed(() => {
        // Use dateRange if set, otherwise default to today's range for stats
        if (dateRange.value) {
          return dateRange.value;
        }
        const today = new Date();
        const startDate = new Date(today);
        const endDate = new Date(today);

        // Set start time to 00:00:00
        startDate.setHours(0, 0, 0, 0);

        // Set end time to 23:59:59
        endDate.setHours(23, 59, 59, 999);

        return {
          start: startDate,
          end: endDate,
        };
      })
    );

    // Removed unnecessary API call - using data from list instead

    // Mutations
    const retryMutation = useRetryStoreTransaction();
    const retryAllMutation = useRetryAllFailedStoreTransactions();

    // Methods
    const handleFilterChange = (newFilters) => {
      filters.value = { ...newFilters };
      pagination.value.currentPage = 1;
    };

    const handleRowClick = (row) => {
      handleViewDetails(row);
    };

    const handleViewDetails = (transaction) => {
      selectedTransaction.value = transaction;
      showDetailsModal.value = true;
    };

    const closeDetailsModal = () => {
      showDetailsModal.value = false;
      selectedTransaction.value = null;
    };

    const handleRetryTransaction = async (uuid) => {
      try {
        await retryMutation.mutateAsync({ uuid, storeId: props.storeId });
        closeDetailsModal();
      } catch (error) {
        console.error("Failed to retry transaction:", error);
        toast.error(
          error?.response?.data?.message || "Failed to retry transaction"
        );
      }
    };

    const handlePageChange = (page) => {
      pagination.value.currentPage = page;
    };

    const handleItemsPerPageChange = (itemsPerPage) => {
      pagination.value.itemsPerPage = itemsPerPage;
      pagination.value.currentPage = 1;
    };

    // Date range methods
    const closeDateFilter = () => {
      showDateFilter.value = false;
    };

    const handleDateRangeFilter = (newDateRange) => {
      // Ensure proper time components are set
      if (newDateRange && newDateRange.start && newDateRange.end) {
        const startDate = new Date(newDateRange.start);
        const endDate = new Date(newDateRange.end);

        // Determine which date is earlier and which is later
        const earlierDate = startDate <= endDate ? startDate : endDate;
        const laterDate = startDate <= endDate ? endDate : startDate;

        // Set earlier date to 00:00:00
        earlierDate.setHours(0, 0, 0, 0);

        // Set later date to 23:59:59
        laterDate.setHours(23, 59, 59, 999);

        dateRange.value = {
          start: earlierDate,
          end: laterDate,
        };
      } else {
        dateRange.value = newDateRange;
      }

      pagination.value.currentPage = 1; // Reset to first page when applying filter
    };

    const formatDateRange = (range) => {
      if (!range || !range.start || !range.end) return "";

      const startDate = new Date(range.start);
      const endDate = new Date(range.end);

      const formatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

      const startStr = startDate.toLocaleDateString("en-US", formatOptions);
      const endStr = endDate.toLocaleDateString("en-US", formatOptions);

      return `${startStr} - ${endStr}`;
    };

    const clearDateRange = () => {
      const today = new Date();
      const startDate = new Date(today);
      const endDate = new Date(today);

      // Set start time to 00:00:00
      startDate.setHours(0, 0, 0, 0);

      // Set end time to 23:59:59
      endDate.setHours(23, 59, 59, 999);

      dateRange.value = {
        start: startDate,
        end: endDate,
      };
      pagination.value.currentPage = 1; // Reset to first page when clearing filter
    };

    // Combined refresh method that refreshes both stats and table data
    const refreshAllData = async () => {
      try {
        // Refresh both stats and transactions data, bypassing cache
        await Promise.all([refetchStats(), refetchTransactions()]);
      } catch (error) {
        console.error("Failed to refresh data:", error);
        toast.error("Failed to refresh data. Please try again.");
      }
    };

    // Computed properties
    const isDarkMode = computed(() => {
      // Check if dark mode is enabled (you may need to adjust this based on your app's dark mode implementation)
      return (
        document.documentElement.classList.contains("dark") ||
        document.body.classList.contains("dark")
      );
    });

    // Watch for transactions data changes to update pagination
    watch(
      transactionsData,
      (newData) => {
        if (newData) {
          pagination.value = {
            currentPage: newData.page || 1,
            totalPages: newData.pages || 1,
            totalItems: newData.total || 0,
            itemsPerPage: newData.per_page || 10,
          };
        }
      },
      { immediate: true }
    );

    // Watch for pagination changes
    watch(
      pagination,
      () => {
        // Pagination updated
      },
      { deep: true, immediate: true }
    );

    // Watch for store changes
    watch(
      () => props.storeId,
      (newStoreId, oldStoreId) => {
        if (newStoreId && newStoreId !== oldStoreId) {
          // Reset filters, pagination, and date range when store changes
          pagination.value.currentPage = 1;
          filters.value = {
            status: "",
            transaction_type: "",
            search: "",
          };
          const today = new Date();
          const startDate = new Date(today);
          const endDate = new Date(today);

          // Set start time to 00:00:00
          startDate.setHours(0, 0, 0, 0);

          // Set end time to 23:59:59
          endDate.setHours(23, 59, 59, 999);

          dateRange.value = {
            start: startDate,
            end: endDate,
          };
        }
      },
      { immediate: false } // Don't run immediately to prevent duplicate requests
    );

    return {
      // Data
      transactionsData: computed(() => transactionsData.value?.items || []),
      transactionsLoading,
      transactionsError,
      statsData,
      statsLoading,
      statsError,
      selectedTransaction: computed(() => selectedTransaction.value),
      pagination,
      showDetailsModal,

      // Date range
      showDateFilter,
      dateRange,
      isDarkMode,

      // Methods
      handleFilterChange,
      handleRowClick,
      handleViewDetails,
      closeDetailsModal,
      handleRetryTransaction,
      handlePageChange,
      handleItemsPerPageChange,
      refetchStats,
      refetchTransactions,
      refreshAllData,
      closeDateFilter,
      handleDateRangeFilter,
      formatDateRange,
      clearDateRange,

      // Mutations
      retryMutation,
      retryAllMutation,
    };
  },
};
</script>

<style scoped>
.ebm-requests {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.ebm-requests__date-card {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.ebm-requests__date-copy {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  min-width: 0;
}

.ebm-requests__date-copy span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ebm-requests__date-copy strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.88rem;
  font-weight: 850;
  margin-top: 0.15rem;
}

:deep(.date-filter__container) {
  margin-top: 136px !important;
}

@media (max-width: 620px) {
  .ebm-requests__date-card {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
